import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { MapPin, Maximize2, BedDouble, Phone, Mail, ExternalLink, User, Clock, ChevronUp, Send, Loader2, ThumbsUp, ThumbsDown, Star, MessageSquare, Trash2, Pencil, ArrowLeftRight, AlertTriangle } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function proxyImage(url) {
  if (!url) return "";
  if (url.includes("subito.it") || url.includes("sbito.it")) {
    return `${API}/image-proxy?url=${encodeURIComponent(url)}`;
  }
  return url;
}

const STATUS_CONFIG = {
  new: { label: "Nuovo", bg: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  interesting: { label: "Interessante", bg: "bg-blue-100 text-blue-800 border-blue-200" },
  contacted: { label: "Contattato", bg: "bg-amber-100 text-amber-800 border-amber-200" },
  acquired: { label: "Acquisito", bg: "bg-yellow-100 text-yellow-800 border-yellow-300" },
  discarded: { label: "Scartato", bg: "bg-zinc-100 text-zinc-500 border-zinc-200" },
};

const AGENT_BADGE = {
  Domenico: "bg-blue-100 text-blue-700 border-blue-200",
  Filippo: "bg-purple-100 text-purple-700 border-purple-200",
  Ivan: "bg-emerald-100 text-emerald-700 border-emerald-200",
  Stefano: "bg-zinc-900 text-white border-zinc-900",
  Giorgia: "bg-pink-100 text-pink-700 border-pink-200",
};

function getAgentForProperty(prop, allComuni) {
  if (prop.assigned_agent) return prop.assigned_agent;
  if (!allComuni) return null;
  const zone = (prop.zone || "").toLowerCase();
  for (const c of allComuni) {
    if (c.agent && zone.includes(c.name.toLowerCase())) return c.agent;
  }
  return null;
}

export default function PropertyCard({ property, allPortals, allComuni, onUpdateStatus, readOnly = false, canChangeCategory = false, canReport = false, onReassign, currentUser }) {
  const [imgError, setImgError] = useState(false);
  const [showTimeline, setShowTimeline] = useState(false);
  const [activities, setActivities] = useState([]);
  const [loadingActs, setLoadingActs] = useState(false);
  const [newNote, setNewNote] = useState("");
  const [sendingNote, setSendingNote] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [editText, setEditText] = useState("");
  const currentUserFirstName = currentUser?.name?.split(" ")[0] || "";
  const isTitolare = currentUser?.role === "titolare";
  const assignedAgentFirstName = (property.assigned_agent || "").split(" ")[0];

  // Per il titolare: viewed=sempre true (bottoni sempre visibili, non devono "aprire prima")
  // Per gli agenti: viewed = loro hanno visto l'immobile O lo stato è cambiato
  const [viewed, setViewed] = useState(
    isTitolare
      ? true
      : (property.viewed_by || []).includes(currentUserFirstName) || property.status !== "new"
  );

  // Badge VISIONATO: si basa sulla view dell'agente assegnato (non del titolare)
  const agentHasViewed = (property.viewed_by || []).includes(assignedAgentFirstName) || property.status !== "new";

  // Sync viewed from prop when properties list refreshes
  useEffect(() => {
    if (isTitolare) return; // il titolare è sempre "viewed"
    if ((property.viewed_by || []).includes(currentUserFirstName)) {
      setViewed(true);
    }
  }, [property.viewed_by, currentUserFirstName, isTitolare]);
  const [currentCategory, setCurrentCategory] = useState(property.property_category || "residenziale");
  const [showReport, setShowReport] = useState(false);
  const [reportReason, setReportReason] = useState("");
  const [sendingReport, setSendingReport] = useState(false);
  const statusConfig = STATUS_CONFIG[property.status] || STATUS_CONFIG.new;
  const agent = getAgentForProperty(property, allComuni);
  const isDiscarded = property.status === "discarded";

  // Load last note on mount
  useEffect(() => {
    axios.get(`${API}/properties/${property.id}/activities`).then(r => {
      const notes = (r.data || []).filter(a => a.action === "note");
      if (notes.length) setLastNoteData(notes[0]);
    }).catch(() => {});
  }, [property.id]);

  const [lastNoteData, setLastNoteData] = useState(null);
  const lastNote = activities.find(a => a.action === "note") || lastNoteData;

  const formatPrice = (price) => new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(price || 0);
  const formatDate = (iso) => {
    if (!iso) return "";
    return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  };
  const formatDateTime = (iso) => {
    if (!iso) return "";
    return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
  };

  const openTimeline = async () => {
    if (!showTimeline) {
      setLoadingActs(true);
      try {
        const [actsRes] = await Promise.all([
          axios.get(`${API}/properties/${property.id}/activities`),
          axios.post(`${API}/properties/${property.id}/view`).catch(() => {}),
        ]);
        setActivities(actsRes.data);
      } catch (e) { console.error(e); }
      setLoadingActs(false);
    }
    setShowTimeline(!showTimeline);
  };

  const addNote = async () => {
    if (!newNote.trim()) return;
    setSendingNote(true);
    try {
      await axios.post(`${API}/properties/${property.id}/activities`, { note: newNote.trim() });
      const res = await axios.get(`${API}/properties/${property.id}/activities`);
      setActivities(res.data);
      const notes = (res.data || []).filter(a => a.action === "note");
      if (notes.length) setLastNoteData(notes[0]);
      setNewNote("");
      toast.success("Nota aggiunta");
    } catch { toast.error("Errore"); }
    setSendingNote(false);
  };

  const deleteNote = async (actId) => {
    try {
      await axios.delete(`${API}/properties/${property.id}/activities/${actId}`);
      setActivities(prev => prev.filter(a => a.id !== actId));
      if (lastNoteData?.id === actId) setLastNoteData(null);
      toast.success("Nota eliminata");
    } catch { toast.error("Errore"); }
  };

  const updateNote = async (actId) => {
    if (!editText.trim()) return;
    try {
      await axios.put(`${API}/properties/${property.id}/activities/${actId}`, { note: editText.trim() });
      setActivities(prev => prev.map(a => a.id === actId ? { ...a, detail: editText.trim() } : a));
      if (lastNoteData?.id === actId) setLastNoteData(prev => ({ ...prev, detail: editText.trim() }));
      setEditingNote(null);
      setEditText("");
      toast.success("Nota aggiornata");
    } catch { toast.error("Errore"); }
  };

  const handleViewListing = async (url) => {
    setViewed(true);
    try {
      await axios.post(`${API}/properties/${property.id}/view`);
    } catch {}
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const setStatus = (status) => {
    onUpdateStatus(property.id, status);
  };

  const toggleCategory = async () => {
    const newCat = currentCategory === "residenziale" ? "commerciale" : "residenziale";
    try {
      await axios.put(`${API}/properties/${property.id}/category`, { category: newCat });
      setCurrentCategory(newCat);
      toast.success(`Categoria cambiata: ${newCat}`);
    } catch { toast.error("Errore cambio categoria"); }
  };

  const sendReport = async () => {
    if (!reportReason.trim()) { toast.error("Scrivi una nota per la segnalazione"); return; }
    setSendingReport(true);
    try {
      await axios.post(`${API}/properties/${property.id}/segnala`, { motivo: reportReason.trim() });
      toast.success("Segnalazione inviata al Titolare");
      setShowReport(false);
      setReportReason("");
    } catch (e) {
      const msg = e.response?.data?.detail || "Errore invio segnalazione";
      toast.error(msg);
    }
    setSendingReport(false);
  };

  const ACTIVITY_ICONS = {
    created: "📥", status_change: "🔄", contacted: "📞", note: "📝", viewed: "👁️", price_change: "💰",
  };

  return (
    <div
      data-testid={`property-card-${property.id}`}
      className={`border rounded-sm transition-all duration-200 ${
        isDiscarded
          ? "border-zinc-100 bg-zinc-50 opacity-40"
          : agentHasViewed && property.status === "new"
          ? "border-amber-200 bg-amber-50/20"
          : "border-zinc-200 hover:shadow-sm"
      }`}
    >
      <div className="flex flex-col sm:flex-row">
        {/* Image */}
        <div className="w-full sm:w-48 shrink-0 relative">
          {property.image_url && !imgError ? (
            <img src={proxyImage(property.image_url)} alt={property.title} className="w-full h-40 sm:h-full object-cover rounded-t-sm sm:rounded-t-none sm:rounded-l-sm" referrerPolicy="no-referrer" onError={() => setImgError(true)} />
          ) : (
            <div className="w-full h-32 sm:h-full bg-gradient-to-br from-zinc-100 to-zinc-200 flex flex-col items-center justify-center rounded-t-sm sm:rounded-t-none sm:rounded-l-sm gap-1">
              <MapPin className="w-5 h-5 text-zinc-300" />
              <span className="text-[9px] text-zinc-400 font-semibold">{property.portals_found?.[0]}</span>
            </div>
          )}
          {/* Viewed badge — mostra se l'agente assegnato ha visionato */}
          {agentHasViewed && (
            <div className="absolute top-1.5 left-1.5 text-[9px] font-bold bg-amber-500 text-white px-1.5 py-0.5 rounded-sm">
              VISIONATO
            </div>
          )}
        </div>

        {/* Details */}
        <div className="flex-1 p-3 sm:p-4 min-w-0">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-1 sm:gap-4">
            <div className="min-w-0">
              <div className="flex items-center gap-1.5 mb-0.5 flex-wrap">
                {property.property_type && (
                  <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-sm border ${currentCategory === "commerciale" ? "text-indigo-700 bg-indigo-50 border-indigo-200" : "text-teal-700 bg-teal-50 border-teal-200"}`}>
                    {property.property_type}
                  </span>
                )}
                {canChangeCategory && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button data-testid={`toggle-category-${property.id}`} onClick={toggleCategory} className="w-5 h-5 flex items-center justify-center rounded-sm border border-zinc-200 hover:bg-zinc-100 text-zinc-400 hover:text-zinc-700 transition-colors">
                        <ArrowLeftRight className="w-3 h-3" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent><p className="text-xs">Cambia: {currentCategory === "residenziale" ? "→ Commerciale" : "→ Residenziale"}</p></TooltipContent>
                  </Tooltip>
                )}
                {property.listing_type === "affitto" && (
                  <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-sm bg-orange-50 text-orange-700 border border-orange-200">Affitto</span>
                )}
                {property.cross_category && (
                  <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-sm bg-red-50 text-red-600 border border-red-200">DOPPIA CATEGORIA</span>
                )}
              </div>
              <h3 className="text-sm sm:text-base font-semibold text-zinc-900 leading-tight">
                {property.ref_code && <span className="font-mono text-xs text-zinc-400 mr-1.5">{property.ref_code}</span>}
                {property.title}
              </h3>
              <div className="flex flex-wrap items-center gap-2 sm:gap-3 mt-1 text-xs sm:text-sm text-zinc-500">
                <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{property.zone}</span>
                <span className="flex items-center gap-1"><Maximize2 className="w-3 h-3" />{property.sqm} m²</span>
                <span className="flex items-center gap-1"><BedDouble className="w-3 h-3" />{property.rooms} locali</span>
                {property.status === "contacted" && (
                  <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-sm bg-amber-100 text-amber-700 border border-amber-200">CONTATTATO</span>
                )}
                {property.status === "interesting" && (
                  <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-sm bg-blue-100 text-blue-700 border border-blue-200">DA CONTATTARE</span>
                )}
                {property.status === "acquired" && (
                  <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-sm bg-yellow-100 text-yellow-700 border border-yellow-300">ACQUISITO</span>
                )}
                {property.status === "new" && (
                  <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-sm bg-blue-100 text-blue-700 border border-blue-200">NUOVO</span>
                )}
              </div>
            </div>
            <div className="flex sm:flex-col items-center sm:items-end gap-2 sm:gap-0 mt-1 sm:mt-0 shrink-0">
              <div className="font-heading text-lg sm:text-xl font-black text-zinc-900 tabular-nums">{formatPrice(property.price)}</div>
              {property.last_price_change && (
                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-sm ${property.last_price_change.change_pct < 0 ? "text-emerald-700 bg-emerald-50 border border-emerald-200" : "text-red-600 bg-red-50 border border-red-200"}`}>
                  {property.last_price_change.change_pct > 0 ? "+" : ""}{property.last_price_change.change_pct}%
                </span>
              )}
              <span className="text-[10px] uppercase tracking-wider font-semibold text-zinc-400">{property.seller_type}</span>
            </div>
          </div>

          {/* Contact info + Agent */}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 pt-2 border-t border-zinc-100">
            {property.contact_info?.name && (
              <div className="flex items-center gap-1.5"><User className="w-3.5 h-3.5 text-zinc-400" /><span className="text-sm font-semibold text-zinc-800">{property.contact_info.name}</span></div>
            )}
            {property.contact_info?.phone && (
              <a href={`tel:${property.contact_info.phone}`} className="flex items-center gap-1.5 text-sm text-zinc-600 hover:text-zinc-900"><Phone className="w-3.5 h-3.5 text-emerald-600" /><span className="font-medium">{property.contact_info.phone}</span></a>
            )}
            {property.contact_info?.email && (
              <a href={`mailto:${property.contact_info.email}`} className="flex items-center gap-1.5 text-sm text-zinc-600 hover:text-zinc-900"><Mail className="w-3.5 h-3.5 text-blue-600" /><span className="font-medium">{property.contact_info.email}</span></a>
            )}
            <div className="ml-auto flex items-center gap-2">
              <div className="flex flex-col items-end">
                {(property.original_published_at || property.published_at) && (
                  <span className="text-[10px] font-semibold text-amber-600 tabular-nums">Pubblicato: {formatDate(property.original_published_at || property.published_at)}</span>
                )}
                <span className="text-[10px] text-zinc-400 tabular-nums">Importato: {formatDate(property.created_at)}</span>
              </div>
              {agent && (
                <span className={`text-[11px] font-bold px-2 py-0.5 rounded-sm border ${AGENT_BADGE[agent] || "bg-zinc-100 text-zinc-600 border-zinc-200"}`}>{agent}</span>
              )}
            </div>
          </div>

          {/* Last note preview */}
          {lastNote ? (
            <div className="mt-2 px-2.5 py-1.5 bg-blue-50/50 border border-blue-100 rounded-sm">
              {editingNote === lastNote.id ? (
                <div className="flex gap-2">
                  <Input value={editText} onChange={e => setEditText(e.target.value)} className="h-7 text-xs rounded-sm flex-1" onKeyDown={e => e.key === "Enter" && updateNote(lastNote.id)} autoFocus />
                  <button onClick={() => updateNote(lastNote.id)} className="text-emerald-600 text-[10px] font-bold">Salva</button>
                  <button onClick={() => setEditingNote(null)} className="text-zinc-400 text-[10px]">Annulla</button>
                </div>
              ) : (
                <div className="flex items-start gap-1.5">
                  <MessageSquare className="w-3 h-3 text-blue-400 mt-0.5 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <span className="text-[9px] font-bold text-blue-500 tabular-nums">{formatDateTime(lastNote.timestamp)}</span>
                    <span className="text-[10px] font-bold text-blue-600 ml-1">{lastNote.user}:</span>
                    <span className="text-[11px] text-zinc-600 ml-1">{lastNote.detail}</span>
                  </div>
                  {!readOnly && (
                    <div className="flex items-center gap-0.5 shrink-0">
                      <button onClick={() => { setEditingNote(lastNote.id); setEditText(lastNote.detail); }} className="w-5 h-5 flex items-center justify-center rounded-sm hover:bg-blue-100 text-zinc-400 hover:text-blue-600">
                        <Pencil className="w-3 h-3" />
                      </button>
                      <button onClick={() => deleteNote(lastNote.id)} className="w-5 h-5 flex items-center justify-center rounded-sm hover:bg-red-100 text-zinc-400 hover:text-red-500">
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="mt-2 px-2.5 py-1.5 bg-zinc-50 border border-zinc-100 rounded-sm flex items-center gap-1.5">
              <MessageSquare className="w-3 h-3 text-zinc-300" />
              <span className="text-[11px] text-zinc-400 italic">Nessuna nota</span>
            </div>
          )}

          {/* Actions row: link + thumbs + cronologia */}
          <div className="flex flex-wrap items-center justify-between mt-2 sm:mt-3 gap-2">
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] font-semibold text-zinc-500">{property.portals_found?.join(" · ")}</span>
              {property.portals_found?.length > 1 && (
                <span className="text-[9px] font-bold text-violet-600 bg-violet-50 px-1.5 py-0.5 rounded-sm border border-violet-200">Su {property.portals_found.length} portali</span>
              )}
            </div>

            <div className="flex items-center gap-1">
              {/* View listing */}
              {Object.values(property.source_urls || {}).filter(Boolean)[0] && (
                <button
                  data-testid={`view-listing-${property.id}`}
                  onClick={() => handleViewListing(Object.values(property.source_urls)[0])}
                  className="h-7 px-2.5 rounded-sm text-xs font-semibold border border-zinc-200 bg-white hover:bg-zinc-50 text-zinc-700 flex items-center gap-1 transition-colors"
                >
                  <ExternalLink className="w-3 h-3" /> Vedi annuncio
                </button>
              )}

              {/* Thumbs — status quick actions: show only after viewing */}
              {!readOnly && viewed && (
                <div className="flex items-center gap-0.5 ml-1">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        data-testid={`thumb-up-${property.id}`}
                        onClick={() => setStatus("interesting")}
                        className={`w-7 h-7 flex items-center justify-center rounded-sm border transition-all ${property.status === "interesting" ? "bg-blue-100 border-blue-300 text-blue-600" : "border-zinc-200 text-zinc-400 hover:text-blue-600 hover:border-blue-200 hover:bg-blue-50"}`}
                      >
                        <ThumbsUp className="w-3.5 h-3.5" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent><p className="text-xs">Interessante</p></TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        data-testid={`thumb-mid-${property.id}`}
                        onClick={() => setStatus("contacted")}
                        className={`w-7 h-7 flex items-center justify-center rounded-sm border transition-all ${property.status === "contacted" ? "bg-amber-100 border-amber-300 text-amber-600" : "border-zinc-200 text-zinc-400 hover:text-amber-600 hover:border-amber-200 hover:bg-amber-50"}`}
                      >
                        <Phone className="w-3.5 h-3.5" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent><p className="text-xs">Contattato</p></TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        data-testid={`thumb-acquired-${property.id}`}
                        onClick={() => setStatus("acquired")}
                        className={`w-7 h-7 flex items-center justify-center rounded-sm border transition-all ${property.status === "acquired" ? "bg-yellow-100 border-yellow-300 text-yellow-600" : "border-zinc-200 text-zinc-400 hover:text-yellow-600 hover:border-yellow-200 hover:bg-yellow-50"}`}
                      >
                        <Star className="w-3.5 h-3.5" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent><p className="text-xs">Acquisito</p></TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        data-testid={`thumb-down-${property.id}`}
                        onClick={() => setStatus("discarded")}
                        className={`w-7 h-7 flex items-center justify-center rounded-sm border transition-all ${property.status === "discarded" ? "bg-red-100 border-red-300 text-red-500" : "border-zinc-200 text-zinc-400 hover:text-red-500 hover:border-red-200 hover:bg-red-50"}`}
                      >
                        <ThumbsDown className="w-3.5 h-3.5" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent><p className="text-xs">Scartato</p></TooltipContent>
                  </Tooltip>
                </div>
              )}
              {/* Hint when not yet viewed */}
              {!readOnly && !viewed && (
                <span className="text-[10px] text-zinc-400 italic ml-1">Apri l'annuncio per valutare</span>
              )}

              {/* Read-only status badge */}
              {readOnly && (
                <span className={`h-7 rounded-sm text-xs font-semibold border px-2 flex items-center ${statusConfig.bg}`}>{statusConfig.label}</span>
              )}

              {/* Report button — agents only: triangle exclamation + "Segnala" */}
              {canReport && (
                <button
                  data-testid={`report-${property.id}`}
                  onClick={() => setShowReport(!showReport)}
                  className={`h-7 px-2 rounded-sm text-xs font-semibold border flex items-center gap-1 transition-all ${showReport ? "bg-orange-100 border-orange-300 text-orange-600" : "border-zinc-200 text-zinc-500 hover:text-orange-600 hover:border-orange-200 hover:bg-orange-50"}`}
                >
                  <AlertTriangle className="w-3.5 h-3.5" /> Segnala
                </button>
              )}

              {/* Cronologia toggle */}
              <button
                data-testid={`expand-timeline-${property.id}`}
                onClick={openTimeline}
                className={`w-7 h-7 flex items-center justify-center rounded-sm border transition-all ${showTimeline ? "bg-zinc-900 border-zinc-900 text-white" : "border-zinc-200 hover:bg-zinc-100 text-zinc-400 hover:text-zinc-700"}`}
              >
                {showTimeline ? <ChevronUp className="w-3.5 h-3.5" /> : <Clock className="w-3.5 h-3.5" />}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Timeline (expanded) */}
      {showTimeline && (
        <div data-testid={`timeline-${property.id}`} className="border-t border-zinc-100 p-3 sm:p-4 bg-zinc-50/50">
          <div className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-2">Cronologia</div>
          
          {loadingActs ? (
            <div className="flex items-center gap-2 py-2"><Loader2 className="w-3 h-3 animate-spin text-zinc-400" /><span className="text-xs text-zinc-400">Caricamento...</span></div>
          ) : activities.length === 0 ? (
            <p className="text-xs text-zinc-400 py-1">Nessuna attività registrata.</p>
          ) : (
            <div className="space-y-1.5">
              {activities.map(act => (
                <div key={act.id} className="flex items-start gap-2 text-xs group">
                  <span className="shrink-0 mt-0.5">{ACTIVITY_ICONS[act.action] || "•"}</span>
                  <div className="flex-1 min-w-0">
                    {editingNote === act.id ? (
                      <div className="flex gap-1">
                        <Input value={editText} onChange={e => setEditText(e.target.value)} className="h-6 text-xs rounded-sm flex-1" onKeyDown={e => e.key === "Enter" && updateNote(act.id)} autoFocus />
                        <button onClick={() => updateNote(act.id)} className="text-emerald-600 text-[10px] font-bold">Salva</button>
                        <button onClick={() => setEditingNote(null)} className="text-zinc-400 text-[10px]">Annulla</button>
                      </div>
                    ) : (
                      <>
                        {act.user && <span className="font-bold text-zinc-700">{act.user}: </span>}
                        <span className="text-zinc-600">{act.detail}</span>
                      </>
                    )}
                  </div>
                  <span className="shrink-0 text-zinc-400 tabular-nums">{formatDate(act.timestamp)}</span>
                  {act.action === "note" && !readOnly && editingNote !== act.id && (
                    <div className="hidden group-hover:flex items-center gap-0.5 shrink-0">
                      <button onClick={() => { setEditingNote(act.id); setEditText(act.detail); }} className="w-5 h-5 flex items-center justify-center rounded-sm hover:bg-zinc-200 text-zinc-400"><Pencil className="w-3 h-3" /></button>
                      <button onClick={() => deleteNote(act.id)} className="w-5 h-5 flex items-center justify-center rounded-sm hover:bg-red-100 text-zinc-400 hover:text-red-500"><Trash2 className="w-3 h-3" /></button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Add note */}
          {!readOnly && (
            <div className="flex gap-2 mt-3 pt-2 border-t border-zinc-200">
              <Input
                data-testid={`note-input-${property.id}`}
                value={newNote}
                onChange={e => setNewNote(e.target.value)}
                placeholder="Aggiungi nota..."
                className="h-8 text-xs rounded-sm border-zinc-300 flex-1"
                onKeyDown={e => e.key === "Enter" && addNote()}
              />
              <Button data-testid={`send-note-${property.id}`} onClick={addNote} disabled={sendingNote || !newNote.trim()} size="sm" className="h-8 w-8 p-0 bg-zinc-900 text-white hover:bg-zinc-800 rounded-sm">
                {sendingNote ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Report form (agent) */}
      {showReport && (
        <div data-testid={`report-form-${property.id}`} className="border-t border-orange-200 p-3 sm:p-4 bg-orange-50/50">
          <div className="text-xs font-semibold text-orange-700 uppercase tracking-wider mb-2">
            <AlertTriangle className="w-3.5 h-3.5 inline mr-1" />Segnala al Titolare
          </div>
          <div className="flex gap-2">
            <Input
              data-testid={`report-input-${property.id}`}
              value={reportReason}
              onChange={e => setReportReason(e.target.value)}
              placeholder="Scrivi la tua segnalazione..."
              className="h-8 text-xs rounded-sm border-zinc-300 flex-1"
              onKeyDown={e => e.key === "Enter" && sendReport()}
              autoFocus
            />
            <Button data-testid={`report-send-${property.id}`} onClick={sendReport} disabled={sendingReport || !reportReason.trim()} size="sm" className="h-8 px-3 bg-orange-600 text-white hover:bg-orange-700 rounded-sm text-xs font-semibold">
              {sendingReport ? <Loader2 className="w-3 h-3 animate-spin" /> : "Invia"}
            </Button>
            <button onClick={() => { setShowReport(false); setReportReason(""); }} className="text-xs text-zinc-400 hover:text-zinc-600">Annulla</button>
          </div>
        </div>
      )}
    </div>
  );
}
